import { useEffect, useState } from 'react';

import { isStandalonePWA } from '../lib/push';

// Testing escape hatch. `?mobile=1` forces mobile mode on, `?mobile=0` forces it
// off — handy for previewing the mobile menu in a desktop browser. Stored in
// sessionStorage (not localStorage) so it survives client-side navigation (the
// router drops the query string) but auto-clears when the tab closes and never
// leaks into the real installed PWA's shared storage.
const OVERRIDE_KEY = 'v2-mobile-app-override';

function readOverride(): boolean | null {
    if (typeof window === 'undefined') return null;
    const param = new URLSearchParams(window.location.search).get('mobile');
    if (param === '1') {
        window.sessionStorage.setItem(OVERRIDE_KEY, '1');
        return true;
    }
    if (param === '0') {
        window.sessionStorage.setItem(OVERRIDE_KEY, '0');
        return false;
    }
    const stored = window.sessionStorage.getItem(OVERRIDE_KEY);
    if (stored === '1') return true;
    if (stored === '0') return false;
    return null;
}

// True when the admin is running as the installed mobile PWA (launched from the
// home screen), or when the `?mobile` override is set. The override wins so you
// can both force-on and force-off regardless of how the app was launched.
export function isMobileApp(): boolean {
    const override = readOverride();
    if (override !== null) return override;
    return isStandalonePWA();
}

// Reactive variant for components. display-mode rarely flips at runtime, but we
// listen so an install/uninstall mid-session is reflected without a reload.
export function useMobileApp(): boolean {
    const [value, setValue] = useState<boolean>(() => isMobileApp());

    useEffect(() => {
        const mql = window.matchMedia?.('(display-mode: standalone)');
        if (!mql) return;
        const onChange = () => setValue(isMobileApp());
        mql.addEventListener?.('change', onChange);
        return () => mql.removeEventListener?.('change', onChange);
    }, []);

    return value;
}
